"use client";

import React, {memo} from "react";
import {
    type Control,
    type FieldArrayPath,
    type FieldPath,
    type FieldValues,
    Controller,
    useFieldArray,
    useWatch,
} from "react-hook-form";
import {Button} from "@root/server/client/esweb/components/ui/button.tsx";
import {Input} from "@root/server/client/esweb/components/ui/input.tsx";
import {Checkbox} from "@root/server/client/esweb/components/ui/checkbox.tsx";
import {Label} from "@root/server/client/esweb/components/ui/label.tsx";
import {cn} from "@root/server/client/esweb/lib/utils.ts";
import {Minus, Plus, Trash2, X} from "lucide-react";
import {RhfInput} from "@root/server/client/esweb/components/ui/rhf/RhfInput.tsx";
import {Field, FieldError} from "@root/server/client/esweb/components/ui/field.tsx";

type MovingHeavyItem = {
    name: string;
    weight: number;
    fitsInElevator: boolean;
};

interface MovingHeavyItemsFieldProps<TFieldValues extends FieldValues> {
    control: Control<TFieldValues>;
    name: FieldArrayPath<TFieldValues>;
    minWeight?: number;
}

const HEAVY_ITEM_PRESETS: Array<Omit<MovingHeavyItem, "fitsInElevator"> & {label: string; fitsInElevator?: boolean}> = [
    {label: "Klavír (250 kg)"     , name: "Klavír", weight: 250},
    {label: "Trezor (180 kg)"     , name: "Trezor", weight: 180},
    {label: "Velká skříň (140 kg)", name: "Velká skříň", weight: 140, fitsInElevator: true},
];

const MIN_WEIGHT_DEFAULT = 100;
const WEIGHT_STEP = 10;

export function MovingHeavyItemsField<TFieldValues extends FieldValues>({
    control,
    name,
    minWeight = MIN_WEIGHT_DEFAULT,
}: MovingHeavyItemsFieldProps<TFieldValues>) {

    const {fields, append, remove} = useFieldArray({
        control,
        name,
    });

    const watchedItems = (useWatch({control, name}) as MovingHeavyItem[] | undefined) ?? [];

    const totalItems = watchedItems.length;

    const handleAddCustom = () => {
        append({
            name: `Břemeno ${fields.length + 1}`,
            weight: minWeight,
            fitsInElevator: false,
        });
    };

    const handleAddPreset = (preset: (typeof HEAVY_ITEM_PRESETS)[number]) => {
        append({
            name: preset.name,
            weight: Math.max(minWeight, preset.weight),
            fitsInElevator: Boolean(preset.fitsInElevator),
        });
    };

    return (
        <div className="space-y-4">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                    <h3 className="text-base font-semibold">Břemena nad {minWeight} kg</h3>
                </div>
            </header>

            {totalItems === 0 ? (
                <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed p-8 text-center">
                    <div className="space-y-1">
                        <p className="font-medium">Zatím nemáte přidaná žádná břemena.</p>
                        <p className="text-sm text-muted-foreground">Začněte přidáním vlastního břemena nebo použijte rychlé předvolby.</p>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                        {HEAVY_ITEM_PRESETS.map((preset) => (
                            <Button key={preset.label} type="button" variant="outline" size="sm" onClick={() => handleAddPreset(preset)}>
                                <Plus className="mr-2 h-4 w-4" /> {preset.label}
                            </Button>
                        ))}
                    </div>
                    <Button type="button" variant="default" onClick={handleAddCustom}>
                        <Plus className="mr-2 h-4 w-4" /> Přidat břemeno
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {HEAVY_ITEM_PRESETS.map((preset) => (
                            <Button key={preset.label} type="button" variant="outline" size="sm" onClick={() => handleAddPreset(preset)}>
                                {preset.label}
                            </Button>
                        ))}
                        <Button type="button" variant="ghost" size="sm" onClick={handleAddCustom}>
                            <Plus className="mr-1 h-4 w-4" /> Vlastní břemeno
                        </Button>
                    </div>

                    <div className="space-y-3">
                        {fields.map((field, index) => (
                            <div key={field.id} className="space-y-3 rounded-lg border bg-muted/40 p-4">
                                <div className="flex flex-wrap items-center gap-3">
                                    <RhfInput {...{
                                        name: `${name}.${index}.name` as FieldPath<TFieldValues>,
                                        control,
                                        inputProps: {
                                            required: true,
                                            className: 'bg-white',
                                            placeholder: 'Např. klavír'
                                        },
                                        className: 'flex-1'
                                    }}/>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => remove(index)}
                                        className="text-destructive hover:text-destructive"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>

                                <div className="flex flex-wrap items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <Controller
                                            control={control}
                                            name={`${name}.${index}.weight` as FieldPath<TFieldValues>}
                                            render={({field, fieldState}) => (
                                                <Field data-invalid={fieldState.invalid}>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => field.onChange(Math.max(minWeight, Number(field.value) - WEIGHT_STEP))}
                                                            aria-label="Snížit váhu"
                                                        >
                                                            <Minus className="h-4 w-4" />
                                                        </Button>
                                                        <Input
                                                            {...field}
                                                            type="number"
                                                            min={minWeight}
                                                            className="w-24 text-center nospin bg-white"
                                                            onChange={(event) => {
                                                                const parsed = Number.parseInt(event.target.value, 10);
                                                                field.onChange(parsed);
                                                            }}
                                                        />
                                                        <span className="text-sm text-muted-foreground">kg</span>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => field.onChange(Math.max(minWeight, Number(field.value) + WEIGHT_STEP))}
                                                            aria-label="Zvýšit váhu"
                                                        >
                                                            <Plus className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    {fieldState.invalid && (
                                                        <FieldError errors={[fieldState.error]} />
                                                    )}
                                                </Field>
                                            )}
                                        />
                                    </div>

                                    <Controller
                                        control={control}
                                        name={`${name}.${index}.fitsInElevator` as FieldPath<TFieldValues>}
                                        render={({field}) => (
                                            <div className={cn("flex items-center gap-2", "pl-1")}> 
                                                <Checkbox
                                                    id={`elevator-${index}`}
                                                    className="bg-white"
                                                    checked={Boolean(field.value)}
                                                    onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                                                />
                                                <Label htmlFor={`elevator-${index}`} className="text-sm font-normal">
                                                    Vejde se do výtahů
                                                </Label>
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <Button type="button" variant="outline" onClick={handleAddCustom} className="w-full">
                        <Plus className="mr-2 h-4 w-4" /> Přidat další břemeno
                    </Button>
                </div>
            )}
        </div>
    );
}
